import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState
} from "react";
import { v4 as uuid } from "uuid";
import { QueryStatus } from "../../app/types";
import {
  AssessmentEvent,
  CompletedAssessmentEvent,
  CreateAnswerEventInput,
  InputType,
  Question,
  ReasonForDecline,
  SubmittedAnswer,
  UpdateAssessmentEventInput,
  ValueType
} from "../../API";
import {
  fetchAssessmentEvent,
  fetchPendingAssessmentsByRater,
  updateAssessmentEvent,
  completeAssessmentRequest as completeAssessmentRequestAPI,
  declineAssessmentRequest as declineAssessmentRequestAPI,
  AssessmentDetails,
  QuestionAnswerOption,
  AnswerOption,
  PendingAssessmentResponse,
  AcgmeProcedureData
} from "./assessmentEventsAPI";
import { createAnswerEvent } from "../AnswerEvents/answerEventsAPI";
import { List, ListItem } from "@mui/material";

export const useUsersPendingAssessmentEvents = (userId?: string) => {
  const [pendingAssessmentEvents, setPendingAssessmentEvents] =
    useState<PendingAssessmentResponse[]>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getPendingAssessmentEvents = async (userId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const results = await fetchPendingAssessmentsByRater(userId);
      setPendingAssessmentEvents(results ?? []);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error fetching pending assessments: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (userId) {
      getPendingAssessmentEvents(userId);
    }
  }, [userId]);

  return {
    pendingAssessmentEvents,
    status,
    error,
    getPendingAssessmentEvents
  };
};

export const useAssessmentEvent = (assessmentEventId?: string) => {
  const [assessmentDetails, setAssessmentDetails] =
    useState<AssessmentDetails | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getAssessmentDetails = async (assessmentEventId: string) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchAssessmentEvent(assessmentEventId);
      setAssessmentDetails(result);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error fetching assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (assessmentEventId) {
      getAssessmentDetails(assessmentEventId);
    }
  }, [assessmentEventId]);

  return {
    assessmentDetails,
    status,
    error
  };
};

export const useDeclineAssessmentRequest = () => {
  const [status, setStatus] = useState<string>("idle");
  const [error, setError] = useState<string>();
  const [declinedAssessmentEvent, setDeclinedAssessmentEvent] = useState<
    string | null
  >(null);

  const declineAssessmentRequest = async (
    assessmentEventId: string,
    reason: ReasonForDecline
  ) => {
    setStatus("loading");
    setError(undefined);

    try {
      const result = await declineAssessmentRequestAPI(
        assessmentEventId,
        reason
      );
      setDeclinedAssessmentEvent(result?.message);
      setStatus(result?.status);
    } catch (error) {
      const errorString = `error declining assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  return {
    status,
    error,
    declinedAssessmentEvent,
    declineAssessmentRequest
  };
};

export const useCompleteAssessmentRequest = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();
  const [completedAssessmentEvent, setCompletedAssessmentEvent] =
    useState<AcgmeProcedureData | null>(null);

  const completeAssessmentRequest = async (
    assessmentEventId: string,
    answers: SubmittedAnswer[]
  ) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await completeAssessmentRequestAPI(
        assessmentEventId,
        answers
      );
      setCompletedAssessmentEvent(result);
      setStatus("succeeded");
    } catch (error) {
      const errorString = `error completing assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  return {
    status,
    error,
    completedAssessmentEvent,
    completeAssessmentRequest
  };
};

// DEV NOTE: Leaving this in the codebase for now so that we can use feature flags to
// determine the behavior.
export const useCompleteAssessmentEvent = () => {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const completeAssessmentEvent = async (
    assessmentEvent: AssessmentEvent,
    sortedQuestions: Question[],
    answers: (AnswerOption | string | undefined)[]
  ) => {
    setStatus("loading");
    setError(null);

    try {
      for (const answerKey in answers) {
        const question = sortedQuestions[answerKey];
        const answer = answers[answerKey];
        const valueType =
          question.inputType && question.inputType === InputType.Radio
            ? ValueType.Int
            : ValueType.String;

        const createAnswerEventInput: CreateAnswerEventInput = {
          assessmentEventId: assessmentEvent.id,
          questionId: question.id,
          valueType: valueType
        };

        let value;
        if (answer) {
          if (typeof answer === "string") {
            value = answer;
          } else {
            value = answer.value;
          }
        }

        // Only update the value field if there's something to update
        if (value) {
          createAnswerEventInput.value = value;
        }

        await createAnswerEvent(createAnswerEventInput);
      }

      const updateAssessmentEventInput: UpdateAssessmentEventInput = {
        id: assessmentEvent.id,
        completionDate: new Date().toISOString()
      };

      await updateAssessmentEvent(updateAssessmentEventInput);

      setStatus("succeeded");
    } catch (error) {
      const errorString = `error completing assessmentEvent: ${JSON.stringify(
        error
      )}`;
      console.error(errorString);
      setError(errorString);
      setStatus("failed");
    }
  };

  return {
    status,
    error,
    completeAssessmentEvent
  };
};

type UpdateAnswerSelectionsProps = {
  question: QuestionAnswerOption;
  currentAnswers: AnswerOption[];
  setAnswers: Dispatch<SetStateAction<AnswerOption[]>>;
  answerOption?: AnswerOption;
  stringValue?: string;
};
export const updateAnswerSelections = ({
  question,
  currentAnswers,
  setAnswers,
  answerOption,
  stringValue
}: UpdateAnswerSelectionsProps) => {
  const inputType = question.inputTypeName;
  // If inputType is Radio and an answer exists in the array for the questionId, replace it. Otherwise, add it.
  if (inputType === InputType.Radio && answerOption) {
    const updatedAnswers = [...currentAnswers];
    const indexOfExistingAnswer = currentAnswers.findIndex(
      (answer) => answer.questionId === question.id
    );
    if (indexOfExistingAnswer > -1) {
      updatedAnswers.splice(indexOfExistingAnswer, 1);
    }
    updatedAnswers.push(answerOption);
    setAnswers(updatedAnswers);
  }
  // If inputType is Checkbox, toggle the answer in the array.
  if (inputType === InputType.Checkbox && answerOption) {
    const updatedAnswers = [...currentAnswers];
    const indexOfExistingAnswer = currentAnswers.findIndex(
      (answer) =>
        answer.questionId === question.id && answer.value === answerOption.value
    );
    if (indexOfExistingAnswer > -1) {
      updatedAnswers.splice(indexOfExistingAnswer, 1);
    } else {
      updatedAnswers.push(answerOption);
    }
    setAnswers(updatedAnswers);
  }
  // If inputType is Feedback, create or update the answer in the array.
  if (inputType === InputType.Feedback) {
    const updatedAnswers = [...currentAnswers];
    const indexOfExistingAnswer = updatedAnswers.findIndex(
      (answer) => answer.questionId === question.id
    );
    if (indexOfExistingAnswer > -1) {
      updatedAnswers[indexOfExistingAnswer].value = stringValue;
    } else {
      updatedAnswers.push({
        id: uuid(),
        questionId: question.id,
        value: stringValue,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    setAnswers(updatedAnswers);
  }
};

export const answerIsSelected = (
  answerOption: AnswerOption,
  currentAnswers: AnswerOption[]
) => {
  return currentAnswers.some(
    (answer) =>
      answer.questionId === answerOption.questionId &&
      answer.value === answerOption.value
  );
};

export const answerDisplay = (
  question: QuestionAnswerOption,
  answers: AnswerOption[]
): ReactNode => {
  const inputType = question.inputTypeName;
  if (!inputType) return "N/A";
  const answersForQuestion = answers.filter(
    (answer) => answer.questionId === question.id
  );
  if (answersForQuestion.length === 0) return "N/A";
  if (inputType === InputType.Checkbox) {
    return (
      <List sx={{ listStyleType: "disc", pl: 4 }}>
        {answersForQuestion.map((answer) => (
          <ListItem sx={{ display: "list-item" }}>
            {answer.anchor ?? "N/A "}
          </ListItem>
        ))}
      </List>
    );
  }
  if (inputType === InputType.Radio) {
    return answersForQuestion[0].anchor;
  }
  if (inputType === InputType.Feedback) {
    return answersForQuestion[0].value ?? "N/A";
  }
};

export const stripUnnecessaryMarkup = (helpText: string) => {
  return helpText.replace(/\\n/gm, "");
};
